import { IconPlugConnected } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * Stacked report sheet waiting for a connection: the motif the other empty
 * states use (approval ledger, keyword sheets), sized for KPI cards and panels.
 */
export function VendorDataIllustration({ className }: { className?: string }): React.ReactElement {
  return (
    <div aria-hidden className={cn("relative h-10 w-14 shrink-0", className)}>
      <div className="absolute top-2.5 left-0.5 h-6 w-8 rounded-md border border-line bg-subtle" />
      <div className="absolute top-1 left-3 flex h-6 w-8 flex-col gap-[3px] rounded-md border border-line bg-surface p-1.5 shadow-[0_2px_6px_#10182814]">
        <span className="h-[3px] w-4 shrink-0 rounded-full bg-gray-300" />
        <span className="h-[3px] w-3 shrink-0 rounded-full bg-gray-200" />
        <span className="h-[3px] w-5 shrink-0 rounded-full bg-gray-200" />
      </div>
      <div className="absolute top-5 left-9 flex size-4.5 items-center justify-center rounded-full bg-primary shadow-[0_0_0_2px_var(--color-surface)]">
        <IconPlugConnected className="size-2.5 text-primary-foreground" stroke={2.2} />
      </div>
    </div>
  );
}

export interface VendorDataEmptyProps {
  /** `compact` fits a KPI card value area; `panel` centers illustration, title and reason. */
  variant?: "compact" | "panel";
  className?: string;
}

/**
 * Explained empty state for an SP-API backed display on a vendor (1P) shop (#679):
 * the data arrives once Vendor Central is connected, so it never shows a blank,
 * a skeleton or a Seller Central authorization prompt.
 */
export function VendorDataEmpty({
  variant = "panel",
  className,
}: VendorDataEmptyProps): React.ReactElement {
  const { t } = useTranslation("common");
  if (variant === "compact") {
    return (
      <div
        data-slot="vendor-data-empty"
        data-variant="compact"
        className={cn("flex min-w-0 items-center gap-2", className)}
      >
        <VendorDataIllustration />
        <span className="min-w-0 text-badge leading-tight text-muted-foreground">
          {t("vendorData.pendingTitle")}
        </span>
      </div>
    );
  }
  return (
    <div
      data-slot="vendor-data-empty"
      data-variant="panel"
      className={cn("flex flex-col items-center gap-3 px-6 py-8 text-center", className)}
    >
      <VendorDataIllustration className="h-[60px] w-[84px] scale-150" />
      <span className="text-sm font-semibold text-ink">{t("vendorData.pendingTitle")}</span>
      <span className="max-w-sm text-xs leading-[18px] text-muted-foreground">
        {t("vendorData.pendingDescription")}
      </span>
    </div>
  );
}
